import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { notification } from "antd";
import ProductService from "services/ProductService";

export const initialState = {
  data: [],
  dataImage: null,
  dataDetail: [],
  dataSelect: [],
  dataDelete: null,
  loading: false,
  error: null,
  hasMore: false,
  status: "idle",
  redirect: false,
};

export const fetchProductData = createAsyncThunk(
  "product/fetchProductData",
  async (data, { rejectWithValue }) => {
    try {
      const { page, pageSize, sort, searchText, state } = data;
      const response = await ProductService.get({
        page,
        pageSize,
        sort,
        searchText,
        state,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const fetchProductDataSelect = createAsyncThunk(
  "product/fetchProductDataSelect",
  async (data, { rejectWithValue }) => {
    try {
      const { page, pageSize, sort, searchText } = data;
      const response = await ProductService.get({
        page,
        pageSize,
        sort,
        searchText,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const fetchProductDetail = createAsyncThunk(
  "product/fetchProductDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ProductService.getById(id);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const fetchProductImage = createAsyncThunk(
  "product/fetchProductImage",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ProductService.getImage(id);
      return response;
    } catch (err) {
      return rejectWithValue(err || "Error");
    }
  }
);

export const createProductData = createAsyncThunk(
  "product/createProductData",
  async (data, { rejectWithValue }) => {
    try {
      const {
        name,
        loyalty_point,
        image,
        type,
        category,
        store_price,
        ecommerce_price_gof,
        ecommerce_price_gbf,
        ecommerce_price_spf,
        ecommerce_price_tkp,
        ecommerce_price_spe,
        ecommerce_price_tts,
        ecommerce_price_qpn,
        days_before_expired,
      } = data;
      const response = await ProductService.createProduct({
        name,
        loyalty_point,
        image,
        type,
        category,
        store_price,
        ecommerce_price_gof,
        ecommerce_price_gbf,
        ecommerce_price_spf,
        ecommerce_price_tkp,
        ecommerce_price_spe,
        ecommerce_price_tts,
        ecommerce_price_qpn,
        days_before_expired,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const updateProductData = createAsyncThunk(
  "product/updateProductData",
  async (data, { rejectWithValue }) => {
    try {
      const {
        id,
        name,
        loyalty_point,
        image,
        type,
        category,
        store_price,
        ecommerce_price_gof,
        ecommerce_price_gbf,
        ecommerce_price_spf,
        ecommerce_price_tkp,
        ecommerce_price_spe,
        ecommerce_price_tts,
        ecommerce_price_qpn,
      } = data;
      const response = await ProductService.updateProduct({
        id,
        name,
        loyalty_point,
        image,
        type,
        category,
        store_price,
        ecommerce_price_gof,
        ecommerce_price_gbf,
        ecommerce_price_spf,
        ecommerce_price_tkp,
        ecommerce_price_spe,
        ecommerce_price_tts,
        ecommerce_price_qpn,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const deleteProductData = createAsyncThunk(
  "product/deleteProductData",
  async (data, { rejectWithValue }) => {
    try {
      const { id, confirm } = data;
      const response = await ProductService.deleteProduct(id, confirm);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    resetDataSelectProduct: (state) => {
      state.dataSelect = [];
    },
    resetDataDeleteProduct: (state) => {
      state.dataDelete = null;
    },
    showLoading: (state) => {
      state.loading = true;
    },
    setImageUrl: (state, action) => {
      state.dataImage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProductData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
      })
      .addCase(fetchProductData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchProductDataSelect.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchProductDataSelect.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.dataSelect.push(...action.payload.data.data);
        const uniqueData = Array.from(
          new Set(state.dataSelect.map((item) => item.name))
        ).map((name) => {
          return state.dataSelect.find((item) => item.name === name);
        });
        state.dataSelect = uniqueData;
        state.hasMore = action.payload.data.next_page_url;
      })
      .addCase(fetchProductDataSelect.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(fetchProductImage.pending, (state) => {
        state.redirect = false;
        state.loading = true;
      })
      .addCase(fetchProductImage.fulfilled, (state, action) => {
        state.loading = false;
        state.dataImage = action.payload;
      })
      .addCase(fetchProductImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchProductDetail.pending, (state) => {
        state.redirect = false;
        state.loading = true;
      })
      .addCase(fetchProductDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.dataDetail = action.payload.data;
        state.error = null;
      })
      .addCase(fetchProductDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createProductData.pending, (state) => {
        state.loading = true;
        state.redirect = false;
      })
      .addCase(createProductData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(createProductData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateProductData.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProductData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        notification.success({ message: "Proses Berhasil" });
        state.redirect = true;
      })
      .addCase(updateProductData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteProductData.pending, (state) => {
        state.redirect = false;
        state.loading = true;
      })
      .addCase(deleteProductData.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.status === 200) state.dataDelete = action.payload;
        else {
          state.redirect = true;
          notification.success({ message: "Proses Berhasil" });
        }
      })
      .addCase(deleteProductData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  resetDataSelectProduct,
  resetDataDeleteProduct,
  showLoading,
  setImageUrl,
} = productSlice.actions;

export default productSlice.reducer;
